import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff, Briefcase, CheckCircle, Check, Ticket } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";

function readInviteFromSearch(searchParams) {
  try {
    const inviteToken = (searchParams.get("invite") || "").trim();
    if (inviteToken) {
      try {
        const jsonStr = decodeURIComponent(escape(atob(inviteToken)));
        const parsed = JSON.parse(jsonStr);
        const code = (parsed.code || "").trim();
        if (code) {
          return {
            code: code.toUpperCase(),
            name: (parsed.name || "").trim(),
            email: (parsed.email || "").trim(),
          };
        }
      } catch (e) {
        console.warn("Failed to decode invite token:", e);
      }
    }

    const code = (searchParams.get("code") || "").trim();
    if (code) {
      return {
        code: code.toUpperCase(),
        name: (searchParams.get("name") || "").trim(),
        email: (searchParams.get("email") || "").trim(),
      };
    }

    return null;
  } catch {
    return null;
  }
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams();

  const codeParam = (searchParams.get("code") || "").trim().toUpperCase();
  const nameParam = (searchParams.get("name") || "").trim();
  const emailParam = (searchParams.get("email") || "").trim();

  const inviteFromToken = (() => {
    const inviteToken = (searchParams.get("invite") || "").trim();
    if (!inviteToken) return null;
    try {
      const jsonStr = decodeURIComponent(escape(atob(inviteToken)));
      const parsed = JSON.parse(jsonStr);
      const c = (parsed.code || "").trim();
      if (c) {
        return {
          code: c.toUpperCase(),
          name: (parsed.name || "").trim(),
          email: (parsed.email || "").trim(),
        };
      }
    } catch {}
    return null;
  })();

  const inviteCode = inviteFromToken ? inviteFromToken.code : codeParam;
  const inviteName = inviteFromToken ? inviteFromToken.name : nameParam;
  const inviteEmail = inviteFromToken ? inviteFromToken.email : emailParam;
  const hasUrlInvite = inviteCode.length > 0;

  const [formData, setFormData] = useState({
    name: inviteName,
    email: inviteEmail,
    password: "",
    confirmPassword: "",
    role: hasUrlInvite ? "expert" : "client",  
    inviteCode: inviteCode,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNewsletter, setAcceptNewsletter] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { register, saveConsents } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasUrlInvite) return;
    setFormData((prev) => ({
      ...prev,
      name: inviteName || prev.name,
      email: inviteEmail || prev.email,
      role: "expert",
      inviteCode: inviteCode,
    }));
  }, [inviteCode, inviteName, inviteEmail, hasUrlInvite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !formData.email || !formData.password) {
      setError("Заполните все обязательные поля");
      return;
    }

    if (!acceptTerms) {
      setError("Необходимо принять условия Пользовательского соглашения и Политику конфиденциальности");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Пароли не совпадают");
      return;
    }

    if (formData.password.length < 6) {
      setError("Пароль должен быть не менее 6 символов");
      return;
    }

    let inviteData = null;
    const hasInviteCode = formData.inviteCode.trim().length > 0;

    if (hasInviteCode) {
      const { data: invite, error: invErr } = await supabase
        .from("invite_codes")
        .select("*")
        .eq("code", formData.inviteCode.trim().toUpperCase())
        .single();

      if (invErr || !invite) {
        setError("Инвайт-код не найден. Проверьте правильность ввода.");
        return;
      }
      if (invite.is_used) {
        setError("Этот инвайт-код уже был использован.");
        return;
      }
      if (new Date(invite.expires_at) < new Date()) {
        setError("Срок действия инвайт-кода истёк.");
        return;
      }
      inviteData = invite;
    }

    const effectiveRole = inviteData ? "expert" : "client";

    setLoading(true);
    try {
      const data = await register(
        formData.email,
        formData.password,
        formData.name,
        effectiveRole,
      );

      if (data.user) {
        saveConsents(data.user.id, formData.email, {
          acceptTerms,
          acceptNewsletter
        });

        if (inviteData) {
          await supabase.from("invite_codes").update({
            is_used: true,
            used_by: data.user.id,
            used_at: new Date().toISOString()
          }).eq("id", inviteData.id);

          const tierToSet = inviteData.expert_tier || "premium";
          const maxRetries = 5;
          let updated = false;

          for (let attempt = 0; attempt < maxRetries; attempt++) {
            const { data: existing } = await supabase
              .from("profiles")
              .select("id")
              .eq("id", data.user.id)
              .maybeSingle();

            if (existing) {
              const { error: updateError } = await supabase
                .from("profiles")
                .update({
                  role: "expert",
                  expert_tier: tierToSet,
                  invite_code_used: inviteData.code,
                  invited_by: inviteData.created_by
                })
                .eq("id", data.user.id);

              if (!updateError) {
                updated = true;
                break;
              }
              console.warn("Profile update attempt failed:", updateError);
            }

            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
          }

          if (!updated) {
            console.error("Failed to set expert tier after retries");
          }
        }
      }

      if (data.session) {
        navigate(effectiveRole === "expert" ? "/expert/dashboard" : "/client/dashboard");
      } else {
        setSuccess(true);
      }
    } catch (err) {
      const msg = err.message || "Ошибка регистрации";
      if (msg.includes("already registered")) {
        setError("Пользователь с таким email уже зарегистрирован");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div id="register_page" className="min-h-screen flex items-center justify-center px-4 py-12 bg-neutral-950">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full max-w-md p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800 backdrop-blur-xl text-center"
        >
          <CheckCircle className="w-16 h-16 text-accent-400 mx-auto mb-6" />
          <h2 className="text-2xl font-medium text-white mb-4">
            Проверьте почту
          </h2>
          <p className="text-lg font-extralight text-neutral-300 mb-6">
            Мы отправили подтверждение на <span className="text-white">{formData.email}</span>. Перейдите по ссылке в письме, чтобы активировать аккаунт.
          </p>

          <div className="flex flex-col gap-3">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 text-lg font-extralight text-primary-400 hover:text-primary-300 transition-colors mt-2"
            >
              Перейти ко входу
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const hasInvite = formData.inviteCode.trim().length > 0;

  return (
    <div id="register_page" className="min-h-screen flex items-center justify-center px-4 py-12 bg-neutral-950">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-lg font-extralight">На главную</span>
        </Link>

        <div className="p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800 backdrop-blur-xl">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-md bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-xl">О</span>
              </div>
            </Link>
            <h1 className="text-2xl font-medium text-white mb-2">
              Регистрация
            </h1>
            <p className="text-lg font-extralight text-neutral-400">
              {hasInvite ? "Регистрация по приглашению" : "Создайте аккаунт клиента"}
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {error && (
              <div className="p-4 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-lg font-extralight">
                {error}
              </div>
            )}

            {hasInvite && (
              <div className="p-4 rounded-md bg-accent-500/10 border border-accent-500/20">
                <div className="flex items-center gap-3">
                  <Ticket className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <div>
                    <p className="text-lg font-extralight text-accent-300">
                      Инвайт-код применён
                    </p>
                    <p className="text-lg font-extralight text-accent-400/70">
                      Вы будете зарегистрированы как эксперт
                    </p>
                  </div>
                </div>
                <code className="block mt-2 text-lg font-medium text-accent-300 tracking-wider">
                  {formData.inviteCode}
                </code>
              </div>
            )}

            <div>
              <label className="block text-lg font-extralight text-neutral-300 mb-2">
                Имя
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ваше имя"
                  className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-lg font-extralight text-neutral-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-lg font-extralight text-neutral-300 mb-2">
                Пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Минимум 6 символов"
                  className="w-full pl-12 pr-12 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-lg font-extralight text-neutral-300 mb-2">
                Подтвердите пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Повторите пароль"
                  className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-3 pt-1">
              <label
                className="flex items-start gap-3 cursor-pointer group"
                onClick={() => setAcceptTerms(!acceptTerms)}
              >
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border transition-colors ${
                    acceptTerms
                      ? "bg-primary-600 border-primary-600"
                      : "border-neutral-600 group-hover:border-neutral-400"
                  }`}
                >
                  {acceptTerms && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <span className="text-lg font-extralight text-neutral-400 leading-snug">
                  Я принимаю условия{" "}
                  <Link
                    to="/terms"
                    onClick={(e) => e.stopPropagation()}
                    className="text-primary-400 hover:text-primary-300 underline underline-offset-2 transition-colors"
                  >
                    Пользовательского соглашения
                  </Link>{" "}
                  и{" "}
                  <Link
                    to="/privacy"
                    onClick={(e) => e.stopPropagation()}
                    className="text-primary-400 hover:text-primary-300 underline underline-offset-2 transition-colors"
                  >
                    Политику конфиденциальности
                  </Link>
                </span>
              </label>

              <label
                className="flex items-start gap-3 cursor-pointer group"
                onClick={() => setAcceptNewsletter(!acceptNewsletter)}
              >
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border transition-colors ${
                    acceptNewsletter
                      ? "bg-primary-600 border-primary-600"
                      : "border-neutral-600 group-hover:border-neutral-400"
                  }`}
                >
                  {acceptNewsletter && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <span className="text-lg font-extralight text-neutral-400 leading-snug">
                  Хочу получать информационную рассылку
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
            >
              {loading ? "Регистрация..." : "Зарегистрироваться"}
            </button>
          </form>

          <p className="text-center text-lg font-extralight text-neutral-400 mt-6">
            Уже есть аккаунт?{" "}
            <Link
              to="/login"
              className="text-primary-400 hover:text-primary-300 transition-colors"
            >
              Войти
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
